// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useContext } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';

// Importing custom pages
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import LicenseAgreements from './shared_components/LicenseAgreements/LicenseAgreements';
import Overview from './pages/Overview/Overview';
import LoadingScreen from './components/LoadingScreen';
import NoRole from './pages/NoRole/NoRole';
import Deliveries from './pages/Deliveries/Deliveries';
import PlantFeed from './pages/PlantFeed/PlantFeed';
import AdminSettings from './pages/AdminSettings/AdminSettings';
import Labeling from './pages/Labeling/Labeling';
import MaterialFlowPage from './pages/MaterialFlow/MaterialFlowPage';
import DeviceStatuses from './pages/DeviceStatuses/DeviceStatuses';
import ResetPasswordAction from './pages/Authentication/ResetPasswordAction';
import LogInsights from './pages/LogInsights/LogInsights';
import SupplierInsights from './pages/SupplierInsights/SupplierInsights';

// Importing context provider. 
import { AuthContext } from './context/AuthContext';
import { auth, firestore } from './context/FirebaseConfig';

function App() {

  // finding the current user
  const { authUser, currentUser, userLoaded, requiredLicenseAgreements } = useContext(AuthContext);

  function getUnsignedLicenseAgreement() {
    /* Check if the user has agreed to all required license agreements */
    for (const agreement of requiredLicenseAgreements) {
      if (!currentUser?.info?.legal?.license_agreement?.[agreement]?.accepted) {
        return agreement;
      }
    }
    return null;
  }

  // Creating a protected route to only allow logged-in users to access the dashboard. 
  const ProtectedRoute = ({children}) => {
    if(!authUser){
      return <Navigate to="/login"/>
    }

    // Check if the user has agreed to all required license agreements
    const unsignedLicenseAgreementDocId = getUnsignedLicenseAgreement();
    if (unsignedLicenseAgreementDocId) {
      return <Navigate to="/license-agreement" state={{licenseAgreementDocId: unsignedLicenseAgreementDocId}} />
    }

    return children
  }

  const LimitedAccessRoute = ({acceptedUserFlags, children}) => {
    if (currentUser && currentUser.info && currentUser.info.user_flags && 
        acceptedUserFlags.some(flag => currentUser.info.user_flags.includes(flag))) {
      return children;
    }
    return <Navigate to="/"/>
  }

  if (authUser && !userLoaded) {
    return (
      <LoadingScreen />
    )
  }

  const preferredHomePage = 
  !(currentUser && currentUser.info && currentUser.info.user_flags) ? 'no-role' :
  ['admin','manager','demo'].some(flag => currentUser.info.user_flags.includes(flag)) ? 'overview' :
  currentUser.info.user_flags.includes('labeler') ? 'labeling' : 
  'no-role'

  return (
    <BrowserRouter>
      <Routes>
        <Route path='/'>
          <Route index element={<Navigate to={preferredHomePage}/>} />
          <Route path="login" element={<Login/>} />
          <Route path="reset-password" element={<ResetPassword/>} />
          <Route path="/reset-password-action" element={<ResetPasswordAction />} />
          <Route path="license-agreement" element={<LicenseAgreements firestore={firestore} auth={auth} uid={currentUser.uid} />} />
          <Route path="loading" element={<ProtectedRoute><LoadingScreen /></ProtectedRoute>} />
          <Route path="no-role" element={<ProtectedRoute><NoRole/></ProtectedRoute>} />
          <Route path="overview" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin','manager']}><Overview/></LimitedAccessRoute></ProtectedRoute>}/>
          <Route path="deliveries" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin','manager']}><Deliveries/></LimitedAccessRoute></ProtectedRoute>}/>
          <Route path="deliveries/:deliveryId" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin','manager']}><Deliveries/></LimitedAccessRoute></ProtectedRoute>}/>
          <Route path="plant-feed" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin','labeler','manager']}><PlantFeed/></LimitedAccessRoute></ProtectedRoute>}/>
          <Route path="labeling" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin','labeler','manager']}><Labeling/></LimitedAccessRoute></ProtectedRoute>} />
          <Route path="admin-settings" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin']}><AdminSettings/></LimitedAccessRoute></ProtectedRoute>} />
          <Route path="material-flow" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin','manager','labeler']}><MaterialFlowPage/></LimitedAccessRoute></ProtectedRoute>} />
          <Route path="material-flow/:timestamp" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin','manager','labeler']}><MaterialFlowPage/></LimitedAccessRoute></ProtectedRoute>} />
          <Route path="observation/:observationId" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin','manager']}><Overview/></LimitedAccessRoute></ProtectedRoute>}/>
          <Route path="device-statuses" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin']}><DeviceStatuses/></LimitedAccessRoute></ProtectedRoute>}/>
          <Route path="log-insights" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin']}><LogInsights/></LimitedAccessRoute></ProtectedRoute>}/>
          <Route path="supplier-insights" element={<ProtectedRoute><LimitedAccessRoute acceptedUserFlags={['admin','manager','beta']}><SupplierInsights/></LimitedAccessRoute></ProtectedRoute>}/>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
